import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  PlayArrow,
  Refresh,
  Info,
  BoltOutlined,
} from '@mui/icons-material';
import { adminService } from '../api/services';

const AutoGenControls = ({ config, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState(null);

  const handleResetLastExecution = async () => {
    if (!window.confirm('¿Resetear última ejecución? Esto permitirá que se ejecute de nuevo hoy.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await adminService.resetLastExecution(config.id);
      setMessage({ type: 'success', text: response.data.message });
      if (onSuccess) onSuccess();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Error al resetear' });
    } finally {
      setLoading(false);
    }
  };

  const handleForceExecute = async () => {
    if (!window.confirm('¿Forzar ejecución inmediata? Esto ignorará todas las restricciones.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await adminService.forceExecute(config.id);
      setMessage({ 
        type: 'success', 
        text: `${response.data.message} - Categoría: ${response.data.category}` 
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Error al ejecutar' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewStatus = async () => {
    setLoading(true);
    setStatusDialog(true);
    try {
      const response = await adminService.getExecutionStatus(config.id);
      setStatus(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al obtener estado' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 1 }}>
          {message.text}
        </Alert>
      )}

      <Box display="flex" gap={1} flexWrap="wrap">
        <Tooltip title="Forzar ejecución inmediata">
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <BoltOutlined />}
            onClick={handleForceExecute}
            disabled={loading}
          >
            Ejecutar Ahora
          </Button>
        </Tooltip>

        <Tooltip title="Resetear última ejecución">
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={handleResetLastExecution}
            disabled={loading}
          >
            Reset
          </Button>
        </Tooltip>

        <Tooltip title="Ver estado detallado">
          <IconButton
            size="small"
            onClick={handleViewStatus}
            disabled={loading}
          >
            <Info />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Status Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Estado de Ejecución: {config.name}</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : status ? (
            <Box>
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Hora Programada
                </Typography>
                <Typography variant="h6">{status.scheduled_time}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Ventana de Ejecución
                </Typography>
                <Typography variant="body2">
                  {status.window_start} - {status.window_end}
                </Typography>
                {status.in_time_window && (
                  <Chip label="En Ventana Ahora" color="success" size="small" sx={{ mt: 0.5 }} />
                )}
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Posts Generados Hoy
                </Typography>
                <Typography variant="body2">
                  {status.posts_today} / {status.max_posts_per_day}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Última Ejecución
                </Typography>
                <Typography variant="body2">
                  {status.last_execution 
                    ? new Date(status.last_execution).toLocaleString('es-VE')
                    : 'Nunca'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Puede Ejecutarse
                </Typography>
                <Box>
                  <Chip 
                    label={status.can_execute ? 'Sí' : 'No'} 
                    color={status.can_execute ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Hora Actual
                </Typography>
                <Typography variant="body2">{status.current_time}</Typography>
              </Box>
            </Box>
          ) : (
            <Alert severity="error">No se pudo cargar el estado</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutoGenControls;
